// Student app shell. Desktop keeps the five primary destinations visible in a
// fixed sidebar; mobile retains the compact drawer. Feature-level destinations
// sit in a contextual tab row instead of competing in the primary navigation.
//
// The chrome itself lives in components/SidebarShell — this file is now just the
// student's configuration of it, plus the streak pill that is genuinely
// student-only. Keeping staff out is RequireSurface's job in the route table, not
// this file's.
import { useLocation } from 'react-router-dom';
import { boardGradeLabel } from '@/lib/auth';
import { useClaims } from '@/hooks/useSession';
import { Icon } from '@/components/garden';
import { NotificationBadge } from '@/components/dashboard/NotificationBadge';
import { SidebarShell, type ShellNavItem, type ShellTab } from '@/components/SidebarShell';
import { useGarden } from '@/hooks/useGarden';
import { isFirstParty } from '@/lib/tenant';

const NAV: ShellNavItem[] = [
  { to: '/today', label: 'Today', icon: 'home' },
  { to: '/learn', label: 'Learn', icon: 'book' },
  { to: '/practice', label: 'Practice', icon: 'dumbbell' },
  { to: '/tests', label: 'Tests & papers', icon: 'file-text' },
  { to: '/progress', label: 'Progress', icon: 'bar-chart' },
];

const SECTION_TABS: Record<string, ShellTab[]> = {
  learn: [
    { to: '/learn', label: 'Subjects' },
    { to: '/learn/basics', label: 'Learn from the basics' },
    { to: '/learn/doubt', label: 'Ask a doubt' },
    // The PDFs a school gives a class. A school workspace only — CWY's own has
    // no principal stocking a shelf, so the tab would only ever be empty there.
    ...(isFirstParty ? [] : [{ to: '/learn/books', label: 'Class books' }]),
  ],
  practice: [
    { to: '/practice', label: 'Practice by topic' },
    { to: '/practice/weak-areas', label: 'Weak areas' },
    { to: '/practice/cards', label: 'Cards' },
    { to: '/practice/weekly-sheet', label: 'Weekly sheet' },
  ],
  tests: [
    { to: '/tests/past-papers', label: 'Past papers' },
    { to: '/tests/assigned', label: 'Assigned' },
    { to: '/tests/diagnostic', label: 'Diagnostic' },
    { to: '/tests/challenge', label: 'Beat your best' },
  ],
};

function sectionFor(pathname: string): string | null {
  if (pathname.startsWith('/learn')) return 'learn';
  if (pathname.startsWith('/practice')) return 'practice';
  if (pathname.startsWith('/tests')) return 'tests';
  if (pathname.startsWith('/reports')) return 'tests';
  return null;
}

export function GardenShell() {
  const { state: garden } = useGarden();
  const location = useLocation();

  const claims = useClaims();

  const section = sectionFor(location.pathname);

  return (
    <SidebarShell
      nav={NAV}
      tabs={section ? SECTION_TABS[section] : undefined}
      homeTo="/today"
      subtitle={boardGradeLabel(claims) || 'Grade 10 · CBSE'}
      footerNav={{ to: '/learn/doubt', label: 'Ask a doubt', icon: 'file-text' }}
      collapseKey="cwy-sidebar-collapsed"
      headerExtras={
        <>
          <span
            className="hidden items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold sm:inline-flex"
            style={{
              background: 'var(--orange-bg)',
              color: '#BA5A17',
              border: '1px solid var(--orange-fill)',
            }}
          >
            <Icon name="flame" size={14} /> <span data-numeric="">{garden.streak}</span>
          </span>
          <NotificationBadge />
        </>
      }
    />
  );
}
